import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEO from '../components/SEO';
import Container from '../components/ui/Container';
import {
  fetchLegalDocByKey,
  docKeyFromUrlSegment,
  LEGACY_URL_SEGMENTS,
} from '../services/airtableService';
import { supportConfig } from '../data/supportConfig';
import './SupportLegal.css';

// content가 체크리스트 JSON([{ key, label, detail }])이면 배열로 파싱, 아니면 null.
// (예: safety_checklist. 나머지 문서는 마크다운 문자열.)
const parseChecklist = (content) => {
  if (typeof content !== 'string') return null;
  const t = content.trim();
  if (!t.startsWith('[')) return null;
  try {
    const arr = JSON.parse(t);
    if (
      Array.isArray(arr) &&
      arr.length > 0 &&
      typeof arr[0] === 'object' &&
      (arr[0].label != null || arr[0].text != null)
    ) {
      return arr;
    }
  } catch {
    /* 마크다운으로 폴백 */
  }
  return null;
};

/**
 * 프로젝트별 법적 문서 페이지 — /support/<project>/<docKey>
 * doc_key(privacy/terms/safety_checklist/…)로 Supabase legal_policies를 조회해
 * 마크다운 또는 체크리스트(JSON)로 렌더한다.
 *
 * ★2026-09-07★ URL 세그먼트 = doc_key 로 통일했다. 구주소 'policy' 로 들어오면
 * 정본 'privacy' 로 replace 이동한다(주소창까지 정본이 되게 — 프리렌더 stub 은
 * JS 없는 쪽·직접 진입을 담당하고, 이건 앱 안에서의 이동을 담당한다).
 */
const SupportLegal = () => {
  const { project, docKey: urlSeg } = useParams();
  const config = supportConfig[project];
  // legal_policies.app 값: 기본은 URL slug, slug와 다르면 config.legalApp 으로 override
  const app = config?.legalApp || project;
  const docKey = docKeyFromUrlSegment(urlSeg);
  const navigate = useNavigate();

  // 구주소(예: /policy)로 들어왔으면 정본(/privacy)으로 갈아탄다.
  // `replace` 인 이유 — 뒤로가기가 구주소로 되튕기지 않게.
  const canonicalSeg = LEGACY_URL_SEGMENTS[urlSeg];
  useEffect(() => {
    if (canonicalSeg) {
      navigate(`/support/${project}/${canonicalSeg}`, { replace: true });
    }
  }, [canonicalSeg, project, navigate]);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'instant' });

    fetchLegalDocByKey(app, docKey)
      .then((d) => {
        if (alive) setDoc(d);
      })
      .catch(() => {
        if (alive) setError('문서를 불러오는 중 오류가 발생했습니다.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [app, docKey]);

  const serviceLabel = config
    ? `${config.serviceNameKo} (${config.serviceName})`
    : project;
  const checklist = doc ? parseChecklist(doc.content) : null;

  return (
    <div className="legal-page">
      <SEO
        title={`${doc?.name || '문서'}${config ? ` · ${config.serviceNameKo}` : ''}`}
        description={`${serviceLabel} ${doc?.name || ''}`.trim()}
        url={`/support/${project}/${urlSeg}`}
        type="website"
      />
      <Container>
        <article className="legal-doc">
          <header className="legal-doc__header">
            <span className="legal-doc__eyebrow">{serviceLabel}</span>
            <h1 className="legal-doc__title">
              {doc?.name || (loading ? '' : '문서')}
            </h1>
            {doc?.version && (
              <p className="legal-doc__service">버전 {doc.version}</p>
            )}
          </header>

          {loading && <p className="legal-doc__status">불러오는 중…</p>}

          {!loading && error && (
            <div className="legal-doc__status legal-doc__status--error">
              <p>{error}</p>
              <button type="button" onClick={() => window.location.reload()}>
                다시 시도
              </button>
            </div>
          )}

          {!loading && !error && doc && checklist && (
            <ul className="legal-checklist">
              {checklist.map((item, i) => (
                <li className="legal-checklist__item" key={item.key || i}>
                  <p className="legal-checklist__label">
                    {item.label || item.text}
                  </p>
                  {item.detail && (
                    <p className="legal-checklist__detail">{item.detail}</p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {!loading && !error && doc && !checklist && (
            <div className="legal-doc__body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.content}</ReactMarkdown>
            </div>
          )}

          {!loading && !error && !doc && (
            <div className="legal-doc__status">
              <p>등록된 문서가 없습니다.</p>
              <Link to={`/support/${project}`} className="legal-doc__back">
                고객센터로 돌아가기
              </Link>
            </div>
          )}
        </article>
      </Container>
    </div>
  );
};

export default SupportLegal;
